import { useCallback, useId, useRef, useState } from 'react';

import { Icon } from '@/components/ui/icon';
import { CONTROL_BASE, CONTROL_SIZE } from '@/components/ui/input';
import { Popover } from '@/components/ui/popover';
import { useClickOutside, useEscapeKey } from '@/hooks';
import { cn } from '@/lib/cn';
import {
  addMonths,
  formatDisplayDate,
  isSameDay,
  parseIsoDate,
  startOfDay,
  toIsoDate,
} from '@/lib/date';

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** How many years one page of the year grid shows: four rows of four. */
const YEAR_PAGE = 16;

/**
 * What the panel is picking right now.
 *
 * Paging a chevron is fine for next month and hopeless for a date of birth,
 * so the month name and the year in the header are themselves buttons that
 * swap the grid for a coarser one.
 */
type PanelMode = 'days' | 'months' | 'years';

export interface DatePickerProps {
  /** ISO `yyyy-mm-dd`, or empty for no selection. */
  value: string;
  onChange: (isoDate: string) => void;
  placeholder?: string;
  invalid?: boolean;
  disabled?: boolean;
  /** Earliest selectable date, ISO. Earlier days render disabled. */
  min?: string;
  max?: string;
  id?: string;
  className?: string;
  'aria-describedby'?: string;
  /**
   * Names the field where no visible label does.
   *
   * Declared rather than assumed: a dashed JSX attribute on a component that
   * does not list it is never type-checked, so every filter bar passing
   * `aria-label="From"` compiled cleanly and reached a screen reader as an
   * unnamed control. Same bug Select had.
   */
  'aria-label'?: string;
  /** Quick-pick buttons, e.g. "+1 year" for a subscription expiry. */
  presets?: { label: string; getDate: () => Date }[];
  /**
   * Shows a clear affordance once a date is set.
   *
   * Only for genuinely optional dates. On a required field an empty state is
   * not a valid answer, so offering the button would be offering a dead end.
   */
  clearable?: boolean;
}

/**
 * A date field you can type into, with a calendar built from scratch -
 * WinDocs never falls back to `<input type="date">`, whose popup cannot be
 * themed and differs on every browser and OS.
 *
 * Typing is the primary way in, and that is the whole point: the control used
 * to be a button opening a calendar whose only navigation was one month at a
 * time, which put a 1985 date of birth roughly four hundred and ninety clicks
 * away and a 2030 due date forty-eight. This app asks for dates of birth,
 * issue dates, expiry dates and due dates constantly.
 *
 * The text is settled when the field is left rather than on every keystroke,
 * so "1" on the way to "13/09/1985" never becomes a date in the year 1.
 *
 * The day grid is a roving-focus composite: one tab stop, arrows move by day,
 * PageUp/PageDown by month, Shift with them by year, Home/End to the ends of
 * the week.
 */
export function DatePicker({
  value,
  onChange,
  placeholder = 'Select a date',
  invalid = false,
  disabled = false,
  min,
  max,
  id,
  className,
  'aria-describedby': describedBy,
  'aria-label': ariaLabel,
  presets,
  clearable = false,
}: DatePickerProps) {
  const generatedId = useId();
  const controlId = id ?? generatedId;

  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<PanelMode>('days');
  const [editing, setEditing] = useState(false);

  const selected = parseIsoDate(value);
  const minDate = parseIsoDate(min ?? '');
  const maxDate = parseIsoDate(max ?? '');

  /* What is in the box, which is not the value until the field is left. */
  const [text, setText] = useState(() => (selected ? formatDisplayDate(selected) : ''));

  /* The month on screen, which moves independently of the selection. */
  const [viewMonth, setViewMonth] = useState(() => startOfMonth(selected ?? new Date()));
  const [focusedDate, setFocusedDate] = useState(() => selected ?? startOfDay(new Date()));

  /**
   * The box follows a value set from outside - the calendar, a preset, a form
   * reset. Done by comparing against the last one seen rather than in an
   * effect, so the text is right on the first render after the change instead
   * of one paint later.
   */
  const [lastValue, setLastValue] = useState(value);
  if (value !== lastValue) {
    setLastValue(value);
    setText(selected ? formatDisplayDate(selected) : '');
  }

  /**
   * Focus moves into the grid when the calendar was opened from the keyboard.
   * A callback ref rather than an effect: the panel is portalled and only
   * exists once open, so the moment it mounts is the only moment to do it.
   */
  const gridNode = useRef<HTMLDivElement | null>(null);
  const wantsGridFocus = useRef(false);

  const attachGrid = useCallback((node: HTMLDivElement | null) => {
    gridNode.current = node;

    if (node && wantsGridFocus.current) {
      wantsGridFocus.current = false;

      /* After this task, not during it. The popover keeps itself
         `visibility: hidden` until its own layout effect has measured where
         to sit, and a hidden element silently refuses focus - so ArrowDown
         opened the calendar and left the caret in the box with the arrow
         keys doing nothing. A timeout rather than `requestAnimationFrame`
         because an occluded window stops painting, and then the frame that
         was going to move focus never comes. */
      setTimeout(() => {
        node.focus();
      }, 0);
    }
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    setMode('days');
  }, []);

  useClickOutside([wrapperRef, panelRef], close, open);
  useEscapeKey(() => {
    close();
    inputRef.current?.focus();
  }, open);

  /**
   * Opens onto the date being typed, else the selected one, else today -
   * rather than wherever the user last browsed. Done in the handler rather
   * than an effect so opening costs one render instead of two.
   */
  function openCalendar(): void {
    const anchor = parseTypedDate(text) ?? selected ?? startOfDay(new Date());

    setViewMonth(startOfMonth(anchor));
    setFocusedDate(anchor);
    setMode('days');
    setOpen(true);
  }

  function isDisabledDate(date: Date): boolean {
    if (minDate && date.getTime() < startOfDay(minDate).getTime()) {
      return true;
    }
    if (maxDate && date.getTime() > startOfDay(maxDate).getTime()) {
      return true;
    }
    return false;
  }

  function commit(date: Date): void {
    if (isDisabledDate(date)) {
      return;
    }

    onChange(toIsoDate(date));
    setText(formatDisplayDate(date));
    close();
    inputRef.current?.focus();
  }

  /**
   * Settles whatever is in the box.
   *
   * Unreadable text, or a date outside the allowed range, puts the existing
   * value back rather than standing: a box showing a date the form does not
   * hold is the one failure mode nobody notices until the record is wrong.
   */
  function commitTyped(): void {
    const raw = text.trim();

    if (!raw) {
      if (value) {
        onChange('');
      }
      return;
    }

    const parsed = parseTypedDate(raw);

    if (!parsed || isDisabledDate(parsed)) {
      setText(selected ? formatDisplayDate(selected) : '');
      return;
    }

    setText(formatDisplayDate(parsed));

    const iso = toIsoDate(parsed);
    if (iso !== value) {
      onChange(iso);
    }
  }

  function onType(next: string): void {
    setText(next);

    /* The calendar previews what has been typed so far, without committing
       it - so a half-typed year does not move the value. */
    const parsed = parseTypedDate(next);

    if (parsed) {
      setViewMonth(startOfMonth(parsed));
      setFocusedDate(parsed);
    }
  }

  function onFieldKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();

        if (open && mode === 'days') {
          gridNode.current?.focus();
        } else {
          wantsGridFocus.current = true;
          openCalendar();
        }
        break;
      case 'Enter':
        /* Settles the text instead of submitting the form around it: the
           value is set from a state update, which a submit handler running on
           the same keystroke would read before it landed. A second Enter
           submits, with the date the field actually holds. */
        event.preventDefault();
        commitTyped();
        close();
        break;
      default:
        break;
    }
  }

  /** Prev/next, which mean a month, a year or a page of years by mode. */
  function step(direction: -1 | 1): void {
    const months = mode === 'days' ? 1 : mode === 'months' ? 12 : YEAR_PAGE * 12;

    setViewMonth((month) => addMonths(month, months * direction));
  }

  function moveFocus(days: number): void {
    const next = new Date(focusedDate);
    next.setDate(next.getDate() + days);
    setFocusedDate(next);
    setViewMonth(startOfMonth(next));
  }

  function moveFocusByMonths(months: number): void {
    setFocusedDate((date) => addMonths(date, months));
    setViewMonth((month) => addMonths(month, months));
  }

  function onGridKeyDown(event: React.KeyboardEvent<HTMLDivElement>): void {
    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        moveFocus(-1);
        break;
      case 'ArrowRight':
        event.preventDefault();
        moveFocus(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        moveFocus(-7);
        break;
      case 'ArrowDown':
        event.preventDefault();
        moveFocus(7);
        break;
      case 'Home':
        event.preventDefault();
        moveFocus(-mondayIndex(focusedDate));
        break;
      case 'End':
        event.preventDefault();
        moveFocus(6 - mondayIndex(focusedDate));
        break;
      case 'PageUp':
        event.preventDefault();
        moveFocusByMonths(event.shiftKey ? -12 : -1);
        break;
      case 'PageDown':
        event.preventDefault();
        moveFocusByMonths(event.shiftKey ? 12 : 1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        commit(focusedDate);
        break;
      default:
        break;
    }
  }

  const days = buildCalendarGrid(viewMonth);
  const today = startOfDay(new Date());
  const viewYear = viewMonth.getFullYear();
  const yearPageStart = Math.floor(viewYear / YEAR_PAGE) * YEAR_PAGE;

  const stepLabels: Record<PanelMode, [string, string]> = {
    days: ['Previous month', 'Next month'],
    months: ['Previous year', 'Next year'],
    years: ['Earlier years', 'Later years'],
  };

  return (
    /* Positioned so the buttons can sit inside the control, and the anchor the
       panel is measured against. */
    <div ref={wrapperRef} className={cn('relative w-full sm:w-auto', className)}>
      <input
        ref={inputRef}
        id={controlId}
        type="text"
        autoComplete="off"
        spellCheck={false}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-invalid={invalid ? true : undefined}
        aria-describedby={describedBy}
        aria-label={ariaLabel}
        /* The format hint replaces the prompt once the field is focused, which
           is the moment it becomes useful and the moment a caller's own
           placeholder ("From", "To") has already done its job. */
        placeholder={editing ? 'dd/mm/yyyy' : placeholder}
        value={text}
        onChange={(event) => {
          onType(event.target.value);
        }}
        onFocus={() => {
          setEditing(true);
        }}
        onBlur={() => {
          setEditing(false);
          commitTyped();
        }}
        onKeyDown={onFieldKeyDown}
        className={cn(
          CONTROL_BASE,
          CONTROL_SIZE,
          /* Room for the buttons sitting inside the field, which are 22px
             each plus the gap - a date long enough to reach them otherwise
             runs underneath the calendar glyph. */
          clearable ? 'pr-13' : 'pr-8',
          invalid ? 'border-danger' : 'border-border-strong hover:border-content-subtle',
        )}
      />

      {/* Siblings of the input rather than children: a button inside an input
          is invalid markup, and browsers resolve it by dropping one. */}
      <span className="absolute inset-y-0 right-1.5 flex items-center gap-0.5">
        {clearable && selected && !disabled && (
          <button
            type="button"
            aria-label="Clear date"
            className="rounded-sm p-1 text-content-subtle hover:text-content"
            onClick={() => {
              onChange('');
              setText('');
              inputRef.current?.focus();
            }}
          >
            <Icon name="close" size={12} />
          </button>
        )}

        <button
          type="button"
          disabled={disabled}
          aria-label={open ? 'Close calendar' : 'Open calendar'}
          aria-haspopup="dialog"
          aria-expanded={open}
          className="rounded-sm p-1 text-content-subtle hover:text-content disabled:cursor-not-allowed"
          onClick={() => {
            if (open) {
              close();
            } else {
              openCalendar();
            }
          }}
        >
          <Icon name="calendar" size={14} />
        </button>
      </span>

      <Popover anchorRef={wrapperRef} open={open} role="dialog" aria-label="Choose a date">
        <div ref={panelRef} className="w-64 p-2">
          <div className="mb-2 flex items-center gap-1">
            <button
              type="button"
              aria-label={stepLabels[mode][0]}
              onClick={() => {
                step(-1);
              }}
              className="rounded-sm p-1 text-content-muted hover:bg-surface-muted hover:text-content"
            >
              <Icon name="chevron-left" size={14} />
            </button>

            <span className="flex flex-1 items-center justify-center gap-1">
              {mode === 'days' && (
                <button
                  type="button"
                  onClick={() => {
                    setMode('months');
                  }}
                  className="rounded-sm px-1.5 py-0.5 text-xs font-semibold text-content hover:bg-surface-muted"
                >
                  {MONTH_NAMES[viewMonth.getMonth()]}
                </button>
              )}

              {mode === 'years' ? (
                <span className="px-1.5 py-0.5 text-xs font-semibold text-content">
                  {yearPageStart} &ndash; {yearPageStart + YEAR_PAGE - 1}
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setMode('years');
                  }}
                  className="rounded-sm px-1.5 py-0.5 text-xs font-semibold text-content tabular-nums hover:bg-surface-muted"
                >
                  {viewYear}
                </button>
              )}
            </span>

            <button
              type="button"
              aria-label={stepLabels[mode][1]}
              onClick={() => {
                step(1);
              }}
              className="rounded-sm p-1 text-content-muted hover:bg-surface-muted hover:text-content"
            >
              <Icon name="chevron-right" size={14} />
            </button>
          </div>

          {mode === 'days' && (
            <div className="mb-1 grid grid-cols-7">
              {WEEKDAYS.map((day) => (
                <span
                  key={day}
                  className="text-center text-2xs font-medium text-content-subtle"
                  aria-hidden="true"
                >
                  {day}
                </span>
              ))}
            </div>
          )}

          {/* One height for all three grids. The popover measures itself once,
              when it mounts, so a panel that had flipped above a field near
              the bottom of the window would sit 40px off after a switch to
              months. */}
          <div className="h-[11.125rem]">
            {mode === 'days' && (
              /* One tab stop for the whole grid; arrows move within it. */
              <div
                ref={attachGrid}
                role="grid"
                tabIndex={0}
                onKeyDown={onGridKeyDown}
                className="grid grid-cols-7 gap-0.5 outline-none"
              >
                {days.map((date) => {
                  const outsideMonth = date.getMonth() !== viewMonth.getMonth();
                  const isSelected = selected ? isSameDay(date, selected) : false;
                  const isFocused = isSameDay(date, focusedDate);
                  const isToday = isSameDay(date, today);
                  const isDisabled = isDisabledDate(date);

                  return (
                    <button
                      key={date.toISOString()}
                      type="button"
                      role="gridcell"
                      tabIndex={-1}
                      aria-selected={isSelected}
                      aria-current={isToday ? 'date' : undefined}
                      disabled={isDisabled}
                      onClick={() => {
                        commit(date);
                      }}
                      className={cn(
                        'h-7 rounded-sm text-xs transition-colors',
                        outsideMonth ? 'text-content-subtle' : 'text-content',
                        !isSelected && !isDisabled && 'hover:bg-surface-muted',
                        isFocused && !isSelected && 'ring-1 ring-ring',
                        isSelected && 'bg-primary font-medium text-primary-content',
                        isToday && !isSelected && 'font-semibold text-primary',
                        isDisabled && 'cursor-not-allowed opacity-35 hover:bg-transparent',
                      )}
                    >
                      {date.getDate()}
                    </button>
                  );
                })}
              </div>
            )}

            {mode === 'months' && (
              <div className="grid h-full grid-cols-3 grid-rows-4 gap-1">
                {MONTH_NAMES.map((name, index) => {
                  const isDisabled = isMonthUnreachable(viewYear, index, minDate, maxDate);
                  const isCurrent = selected
                    ? selected.getFullYear() === viewYear && selected.getMonth() === index
                    : false;

                  return (
                    <button
                      key={name}
                      type="button"
                      aria-label={`${name} ${String(viewYear)}`}
                      disabled={isDisabled}
                      onClick={() => {
                        setViewMonth(new Date(viewYear, index, 1));
                        setMode('days');
                      }}
                      className={cn(
                        'rounded-sm text-xs transition-colors',
                        isCurrent
                          ? 'bg-primary font-medium text-primary-content'
                          : 'text-content hover:bg-surface-muted',
                        isDisabled && 'cursor-not-allowed opacity-35 hover:bg-transparent',
                      )}
                    >
                      {name.slice(0, 3)}
                    </button>
                  );
                })}
              </div>
            )}

            {mode === 'years' && (
              <div className="grid h-full grid-cols-4 grid-rows-4 gap-1">
                {Array.from({ length: YEAR_PAGE }, (_, index) => yearPageStart + index).map(
                  (year) => {
                    const isDisabled = isYearUnreachable(year, minDate, maxDate);
                    const isCurrent = selected ? selected.getFullYear() === year : false;

                    return (
                      <button
                        key={year}
                        type="button"
                        disabled={isDisabled}
                        onClick={() => {
                          setViewMonth(new Date(year, viewMonth.getMonth(), 1));
                          setMode('months');
                        }}
                        className={cn(
                          'rounded-sm text-xs tabular-nums transition-colors',
                          isCurrent
                            ? 'bg-primary font-medium text-primary-content'
                            : 'text-content hover:bg-surface-muted',
                          isDisabled && 'cursor-not-allowed opacity-35 hover:bg-transparent',
                        )}
                      >
                        {year}
                      </button>
                    );
                  },
                )}
              </div>
            )}
          </div>

          {presets && presets.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1 border-t border-border pt-2">
              {presets.map((preset) => (
                <button
                  key={preset.label}
                  type="button"
                  onClick={() => {
                    commit(startOfDay(preset.getDate()));
                  }}
                  className="rounded-sm px-1.5 py-1 text-2xs text-content-muted hover:bg-surface-muted hover:text-content"
                >
                  {preset.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </Popover>
    </div>
  );
}

/**
 * Reads a date out of whatever somebody typed.
 *
 * Deliberately forgiving, because the alternative was clicking, and clicking
 * is what made this control unusable: separators can be `/`, `-`, `.` or a
 * space, the month can be a number or a name, and a bare run of digits
 * (`13091985`) is accepted because that is how anybody entering a hundred
 * rows actually types.
 *
 * Ambiguity resolves day-first - the app displays `13 Sep 2026` and the firms
 * using it are in the UAE - except where a four-digit year leads, which is
 * read as ISO so a pasted `1985-09-13` also works. A two-digit year below 70
 * is this century and 70 or above is the last, which is what puts a typed
 * `85` in 1985 and a typed `30` in 2030.
 *
 * Lives here rather than in `lib/date` because it is the field's own input
 * grammar, not a way to handle dates the app stores.
 */
function parseTypedDate(input: string, reference: Date = new Date()): Date | null {
  const text = input.trim();

  if (!text) {
    return null;
  }

  const keyed = /^(\d{2})(\d{2})(\d{2}|\d{4})$/.exec(text);

  if (keyed) {
    return buildDate(expandYear(keyed[3] ?? '', reference), Number(keyed[2]), Number(keyed[1]));
  }

  const tokens = text.split(/[^0-9a-z]+/i).filter(Boolean);

  if (tokens.length < 2 || tokens.length > 3) {
    return null;
  }

  const wordAt = tokens.findIndex((token) => !/^\d+$/.test(token));

  if (wordAt < 0) {
    const [first = '', second = '', third] = tokens;

    if (third === undefined) {
      return buildDate(reference.getFullYear(), Number(second), Number(first));
    }

    return first.length === 4
      ? buildDate(Number(first), Number(second), Number(third))
      : buildDate(expandYear(third, reference), Number(second), Number(first));
  }

  /* A second word means it is prose, not a date. */
  if (tokens.some((token, index) => index !== wordAt && !/^\d+$/.test(token))) {
    return null;
  }

  const month = monthFromName(tokens[wordAt] ?? '');

  if (month === null) {
    return null;
  }

  const numbers = tokens.filter((_, index) => index !== wordAt);
  const [first = '', second] = numbers;

  /* "2026 Sep 13" as readily as "13 Sep 2026" or "Sep 13 2026". */
  if (first.length === 4 && second !== undefined) {
    return buildDate(Number(first), month + 1, Number(second));
  }

  const year = second === undefined ? reference.getFullYear() : expandYear(second, reference);

  return buildDate(year, month + 1, Number(first));
}

/** Zero-based month for a name or any prefix of three letters or more. */
function monthFromName(token: string): number | null {
  if (token.length < 3) {
    return null;
  }

  const lower = token.toLowerCase();
  const index = MONTH_NAMES.findIndex((name) => name.toLowerCase().startsWith(lower));

  return index < 0 ? null : index;
}

function expandYear(raw: string, reference: Date): number {
  const year = Number(raw);

  if (raw.length > 2) {
    return year;
  }

  /* 00-69 is this century, 70-99 the last - the Y2K-era window, and the one
     that puts a date of birth where a person expects it. Anchored on the
     reference so it does not need revisiting in 2100. */
  const century = Math.floor(reference.getFullYear() / 100) * 100;

  return year < 70 ? century + year : century - 100 + year;
}

function buildDate(year: number, month: number, day: number): Date | null {
  if (year < 1000 || year > 9999 || month < 1 || month > 12 || day < 1 || day > 31) {
    return null;
  }

  const date = new Date(year, month - 1, day);

  /* `new Date(2026, 1, 31)` is the 3rd of March rather than an error, so a day
     that rolled into the next month was never the date that was typed. */
  if (date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }

  return date;
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

/** True when no day of the month falls inside `min`..`max`. */
function isMonthUnreachable(
  year: number,
  month: number,
  minDate: Date | null,
  maxDate: Date | null,
): boolean {
  const last = new Date(year, month + 1, 0);
  const first = new Date(year, month, 1);

  if (minDate && last.getTime() < startOfDay(minDate).getTime()) {
    return true;
  }

  return Boolean(maxDate && first.getTime() > startOfDay(maxDate).getTime());
}

function isYearUnreachable(year: number, minDate: Date | null, maxDate: Date | null): boolean {
  if (minDate && year < minDate.getFullYear()) {
    return true;
  }

  return Boolean(maxDate && year > maxDate.getFullYear());
}

/** 0 = Monday, matching the header row, rather than JS's Sunday-first. */
function mondayIndex(date: Date): number {
  return (date.getDay() + 6) % 7;
}

/**
 * Six weeks of dates covering the month, padded from the preceding and
 * following months. A fixed 42 cells keeps the panel from resizing as the user
 * pages through months.
 */
function buildCalendarGrid(month: Date): Date[] {
  const first = startOfMonth(month);
  const start = new Date(first);
  start.setDate(first.getDate() - mondayIndex(first));

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    return date;
  });
}
